import PropTypes from "prop-types";
import React from "react";
import { FormFeedback, Input, Label, Spinner } from "reactstrap";

/**
 * Select fed by an async query, with an explicit failed state.
 *
 * Two problems this fixes, both reported during testing:
 *  - the field used Bootstrap's `form-control` instead of `form-select`, so it
 *    rendered without a caret and did not read as a dropdown at all;
 *  - it was `disabled` whenever the query was loading OR had failed, so a
 *    network/auth hiccup left a greyed-out field showing only the placeholder,
 *    with no explanation and no way to recover.
 *
 * On failure it now shows the reason and a retry button instead.
 */
const AsyncSelectField = ({
  label,
  name,
  value,
  onChange,
  onBlur,
  options,
  optionValueKey = "id",
  optionLabelKey = "label",
  placeholder = "Sélectionner",
  required = false,
  isLoading = false,
  isError = false,
  onRetry = null,
  errorText = "Impossible de charger la liste.",
  emptyText = "Aucun élément disponible.",
  invalid = false,
  feedback = null,
  disabled = false,
  hint = null,
}) => {
  const items = Array.isArray(options) ? options : [];
  const isEmpty = !isLoading && !isError && items.length === 0;

  return (
    <div className="mb-3">
      {label ? (
        <Label className="form-label" htmlFor={name}>
          {label} {required ? <span style={{ color: "red" }}>*</span> : null}
        </Label>
      ) : null}

      {isError ? (
        <div className="alert alert-warning d-flex align-items-center justify-content-between mb-0">
          <span>
            <i className="ri-error-warning-line align-bottom me-1"></i>
            {errorText}
          </span>
          {onRetry ? (
            <button
              type="button"
              className="btn btn-sm btn-outline-warning ms-3 flex-shrink-0"
              onClick={onRetry}
            >
              <i className="ri-refresh-line align-bottom me-1"></i>
              Réessayer
            </button>
          ) : null}
        </div>
      ) : (
        <>
          <div className="position-relative">
            <Input
              id={name}
              className="form-select"
              name={name}
              type="select"
              value={value ?? ""}
              onChange={onChange}
              onBlur={onBlur}
              // Only disabled while loading — never on failure, which would
              // hide the problem behind an inert field.
              disabled={disabled || isLoading}
              invalid={invalid}
            >
              <option value="">
                {isLoading ? "Chargement..." : placeholder}
              </option>
              {items.map((item) => (
                <option value={item[optionValueKey]} key={item[optionValueKey]}>
                  {item[optionLabelKey]}
                </option>
              ))}
            </Input>
            {isLoading ? (
              <Spinner
                size="sm"
                className="position-absolute top-50 translate-middle-y"
                style={{ right: "2.25rem" }}
              />
            ) : null}
          </div>

          {invalid && feedback ? (
            <FormFeedback type="invalid" className="d-block">
              {feedback}
            </FormFeedback>
          ) : null}

          {isEmpty ? (
            <small className="text-muted">{emptyText}</small>
          ) : hint ? (
            <small className="text-muted">{hint}</small>
          ) : null}
        </>
      )}
    </div>
  );
};

AsyncSelectField.propTypes = {
  label: PropTypes.node,
  name: PropTypes.string.isRequired,
  value: PropTypes.any,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  options: PropTypes.array,
  optionValueKey: PropTypes.string,
  optionLabelKey: PropTypes.string,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  isLoading: PropTypes.bool,
  isError: PropTypes.bool,
  onRetry: PropTypes.func,
  errorText: PropTypes.string,
  emptyText: PropTypes.string,
  invalid: PropTypes.bool,
  feedback: PropTypes.node,
  disabled: PropTypes.bool,
  hint: PropTypes.node,
};

export default AsyncSelectField;
